import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { type PayloadAction } from "@reduxjs/toolkit";
import { trpc } from "@utils/api";
import { type inferRouterInputs } from "@trpc/server";
import { type AppRouter } from "@server/api/root";
import { ResourceStatus } from "skedyul-db/enums";
import { type RootState } from "@store/index";

type RouterInput = inferRouterInputs<AppRouter>;
export type NewProduct = RouterInput["products"]["createProduct"];
export type UpdateProduct = RouterInput["products"]["updateProduct"];

export type FieldErrorCode = "REQUIRED" | "INVALID" | "OUT_OF_RANGE";

export interface productState {
  product: {
    isLoading: boolean;
    isSaving: boolean;
    data: NewProduct | UpdateProduct;
    fieldErrors: {
      name?: FieldErrorCode;
      handle?: FieldErrorCode;
    };
  };
  products: {
    isLoading: boolean;
    data: UpdateProduct[];
    meta: {
      total: number;
      page: number;
      pages: number;
      limit: number;
    };
  };
}

const initialState: productState = {
  product: {
    isLoading: true,
    isSaving: false,
    data: {
      name: "",
      handle: "",
      variants: [],
      status: ResourceStatus.DRAFT,
    },
    fieldErrors: {},
  },
  products: {
    isLoading: false,
    data: [],
    meta: {
      total: 0,
      page: 0,
      pages: 0,
      limit: 25,
    },
  },
};

export const getProduct = createAsyncThunk(
  "product/get",
  async (payload: string, { getState }): Promise<NewProduct | null> => {
    const {
      products: {
        product: { data: product },
      },
    } = getState() as RootState;

    if (payload == "new") {
      return await trpc.products.getNewProduct.query();
    }

    if ("id" in product && product.id === payload) {
      return product;
    }

    return await trpc.products.getProduct.query(payload);
  },
);

export const getProducts = createAsyncThunk(
  "product/getList",
  async (payload: { q: string; limit: string; page: string }) => {
    return await trpc.products.getProducts.query({
      limit: Number(payload.limit) || 25,
      page: Number(payload.page) || 0,
      q: payload.q,
    });
  },
);

export const createProduct = createAsyncThunk(
  "product/create",
  async (_, { getState }) => {
    const {
      products: {
        product: { data: product },
      },
    } = getState() as RootState;
    return await trpc.products.createProduct.mutate(product);
  },
);

export const updateProduct = createAsyncThunk(
  "product/update",
  async (_, { getState }) => {
    const {
      products: {
        product: { data: product },
      },
    } = getState() as RootState;

    const p = { ...product } as UpdateProduct;

    return await trpc.products.updateProduct.mutate(p);
  },
);

export const productSlice = createSlice({
  name: "product",
  initialState,
  reducers: {
    updateName(state, action: PayloadAction<string>) {
      if (state.product.data) {
        state.product.data.name = action.payload;

        if (!action.payload) {
          state.product.fieldErrors.name = "REQUIRED";
        } else {
          state.product.fieldErrors.name = undefined;
        }
      }
    },
    updateStatus(state, action: PayloadAction<ResourceStatus>) {
      state.product.data.status = action.payload;
    },
    updateAmountField(
      state,
      action: PayloadAction<{
        id: string;
        value: { amount: number; currency: string };
      }>,
    ) {
      if (state.product.data && state.product.data.variants[0]) {
        state.product.data.variants[0].price = action.payload.value;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getProduct.pending, (state) => {
      state.product.isLoading = true;
    });
    builder.addCase(getProduct.fulfilled, (state, action) => {
      state.product.isLoading = false;
      if (action.payload) {
        state.product.data = action.payload;
      }
    });
    builder.addCase(getProduct.rejected, (state) => {
      state.product.isLoading = false;
    });
    builder.addCase(getProducts.pending, (state) => {
      state.products.isLoading = true;
    });
    builder.addCase(getProducts.fulfilled, (state, action) => {
      state.products.isLoading = false;
      if (action.payload) {
        state.products.data = action.payload.records;
        state.products.meta = action.payload.meta;
      }
    });
    builder.addCase(getProducts.rejected, (state) => {
      state.products.isLoading = false;
    });
    builder.addCase(createProduct.pending, (state) => {
      state.product.isSaving = true;
      state.product.fieldErrors.name = undefined;
    });
    builder.addCase(createProduct.fulfilled, (state, action) => {
      state.product.isSaving = false;
      if (action.payload) {
        state.product.data = action.payload;
      }
    });
    builder.addCase(createProduct.rejected, (state) => {
      state.product.isSaving = false;
      state.product.fieldErrors.name = "REQUIRED";
    });
    builder.addCase(updateProduct.pending, (state) => {
      state.product.isSaving = true;
      state.product.fieldErrors.name = undefined;
    });
    builder.addCase(updateProduct.fulfilled, (state, action) => {
      state.product.isSaving = false;
      if (action.payload) {
        state.product.data = action.payload;
      }
    });
    builder.addCase(updateProduct.rejected, (state) => {
      state.product.isSaving = false;
      state.product.fieldErrors.name = "REQUIRED";
    });
  },
});

// Action creators are generated for each case reducer function
export const { updateName, updateAmountField, updateStatus } =
  productSlice.actions;

export default productSlice.reducer;
